import React, { useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { TableColumn } from "react-data-table-component";
import { Button, Badge, Card, Modal, Toast, ToastContainer, Dropdown, Form, Alert } from 'react-bootstrap';
import { Modal as BootstrapModal } from 'bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { GetLeadsByStatusStatus, LoanLead } from "../../../services/schemas/loanServiceSchemas";
import { useGetLeadsByStatus, useSearchLeads, cancelLead, useGetLatestLeadNotes } from "../../../services/loanService";
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import type { SearchLeadsParams, SearchLeadsStatus } from "../../../services/schemas/loanServiceSchemas";
// Reuse the Actions dropdown styling (action-view / action-deactivate colours,
// no-caret toggle) from the Loan Customers page so the design matches.
import LeadNotesModal from './LeadNotesModal';
import EditLeadModal from './EditLeadModal';
import '../customers/LoanCustomers.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_META: Record<string, { label: string; variant: string; icon: string }> = {
    NEW:       { label: 'New',       variant: 'primary',   icon: 'fa-star' },
    COMPLETED: { label: 'Completed', variant: 'success',   icon: 'fa-check-circle' },
    REJECTED:  { label: 'Rejected',  variant: 'danger',    icon: 'fa-times-circle' },
    EXPIRED:   { label: 'Expired',   variant: 'secondary', icon: 'fa-clock-o' },
    CANCELLED: { label: 'Cancelled', variant: 'dark',      icon: 'fa-ban' },
};

/** A lead still open can be cancelled; one that became an application is cancelled there instead. */
const canCancelLead = (status?: string) => status === 'NEW' || status === 'EXPIRED';

// Where the lead originated (Channel column).
const CHANNEL_META: Record<string, { label: string; icon: string; bg: string; color: string }> = {
    ONLINE_CONTACT_FORM:     { label: 'Website', icon: 'fa-globe', bg: '#e3f2fd', color: '#1565c0' },
    ONLINE_APPLICATION_FORM: { label: 'Website', icon: 'fa-globe', bg: '#e8f5e9', color: '#2e7d32' },
    WHATSAPP:                { label: 'WhatsApp',              icon: 'fa-whatsapp', bg: '#e8f5e9', color: '#128C7E' },
    FACEBOOK:                { label: 'Facebook',              icon: 'fa-facebook', bg: '#e7ecf5', color: '#1877F2' },
    LMS:                     { label: 'LMS',                   icon: 'fa-desktop',  bg: '#f3e5f5', color: '#6a1b9a' },
    WEBSITE:                 { label: 'Website',               icon: 'fa-globe',    bg: '#e8f5e9', color: '#2e7d32' },
    PHONE:                   { label: 'Phone',                 icon: 'fa-phone',    bg: '#fff3e0', color: '#e65100' },
    CAMPAIGN:                { label: 'Campaign',              icon: 'fa-bullhorn', bg: '#fce4ec', color: '#ad1457' },
};

// Format a timestamp as yyyy/mm/dd hh:mm:ss (24h).
const formatDateTime = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-ZA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
};

// Advanced-search form shape — one entry per column shown on the leads table.
interface LeadSearch {
    customerReference: string;
    company: string;
    registrationNumber: string;
    contactPerson: string;
    loanType: string;
    status: string;
    amountFrom: string;
    amountTo: string;
    createdFrom: string;
    createdTo: string;
}
const EMPTY_SEARCH: LeadSearch = {
    customerReference: '', company: '', registrationNumber: '', contactPerson: '', loanType: '',
    status: '', amountFrom: '', amountTo: '', createdFrom: '', createdTo: '',
};

const Leads: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Advanced search — mirrors the Loan Customers page: a toggle button reveals a
    // search card whose fields map to the leads table columns.
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [formValues, setFormValues] = useState<LeadSearch>(EMPTY_SEARCH);
    // null = not searching (show the default list); object = a server-side search is active.
    const [searchParams, setSearchParams] = useState<SearchLeadsParams | null>(null);
    const isSearching = searchParams !== null;

    // Default listing when idle: the leads that are still worth acting on — New and
    // Completed. The endpoint takes one status per call, so the two lists are fetched
    // separately and merged; narrowing to a single status is left to Advanced Search.
    const { data: newData, isLoading: newLoading, error: newError } = useGetLeadsByStatus(
        { status: GetLeadsByStatusStatus.NEW, page: 0, size: 100 },
        { query: { enabled: !isSearching } },
    );
    const { data: completedData, isLoading: completedLoading, error: completedError } = useGetLeadsByStatus(
        { status: GetLeadsByStatusStatus.COMPLETED, page: 0, size: 100 },
        { query: { enabled: !isSearching } },
    );
    const { data: searchData, isLoading: searchLoading, error: searchError } = useSearchLeads(
        searchParams ?? {}, { query: { enabled: isSearching } },
    );
    const listLeads = [...(newData?.content ?? []), ...(completedData?.content ?? [])];
    const leads     = isSearching ? (searchData?.content ?? []) : listLeads;
    const isLoading = isSearching ? searchLoading : (newLoading || completedLoading);
    const error     = isSearching ? searchError : (newError ?? completedError);

    // Per-status counts for the summary cards.
    const { data: newCount }       = useGetLeadsByStatus({ status: GetLeadsByStatusStatus.NEW,       page: 0, size: 1 });
    const { data: completedCount } = useGetLeadsByStatus({ status: GetLeadsByStatusStatus.COMPLETED, page: 0, size: 1 });
    const { data: rejectedCount }  = useGetLeadsByStatus({ status: GetLeadsByStatusStatus.REJECTED,  page: 0, size: 1 });
    const { data: expiredCount }   = useGetLeadsByStatus({ status: GetLeadsByStatusStatus.EXPIRED,   page: 0, size: 1 });
    const { data: cancelledCount } = useGetLeadsByStatus({ status: GetLeadsByStatusStatus.CANCELLED, page: 0, size: 1 });
    const counts = {
        NEW:       newCount?.totalElements       ?? 0,
        COMPLETED: completedCount?.totalElements ?? 0,
        REJECTED:  rejectedCount?.totalElements  ?? 0,
        EXPIRED:   expiredCount?.totalElements   ?? 0,
        CANCELLED: cancelledCount?.totalElements ?? 0,
    };
    const totalLeads = counts.NEW + counts.COMPLETED + counts.REJECTED + counts.EXPIRED + counts.CANCELLED;

    // Filtering happens server-side; sort newest-first so the latest lead is on top.
    const displayedLeads = [...leads].sort((a, b) => {
        const ta = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return tb - ta;
    });

    // The last note for every lead on screen, fetched in one call so the column costs one
    // request per page rather than one per row.
    const displayedLeadIds = displayedLeads.map(lead => lead.id).filter((id): id is string => !!id);
    const { data: latestNotes } = useGetLatestLeadNotes(
        { leadIds: displayedLeadIds },
        { query: { enabled: displayedLeadIds.length > 0 } }
    );
    const lastNoteByLeadId = new Map((latestNotes ?? []).map(note => [note.leadId, note]));

    const handleSearch = () => {
        const p: SearchLeadsParams = { page: 0, size: 100 };
        if (formValues.customerReference.trim()) p.customerReference = formValues.customerReference.trim();
        if (formValues.company.trim())           p.company = formValues.company.trim();
        if (formValues.registrationNumber.trim()) p.registrationNumber = formValues.registrationNumber.trim();
        if (formValues.contactPerson.trim())     p.contactName = formValues.contactPerson.trim();
        if (formValues.loanType)                 p.loanType = formValues.loanType;
        if (formValues.status)                   p.status = formValues.status as SearchLeadsStatus;
        if (formValues.amountFrom)               p.amountFrom = Number(formValues.amountFrom);
        if (formValues.amountTo)                 p.amountTo = Number(formValues.amountTo);
        if (formValues.createdFrom)              p.createdDateFrom = formValues.createdFrom;
        if (formValues.createdTo)                p.createdDateTo = formValues.createdTo;
        setSearchParams(p);
    };
    const handleReset = () => { setFormValues(EMPTY_SEARCH); setSearchParams(null); };

    // A summary card shows its leads: the same search as picking that status in Advanced Search.
    const showStatus = (status?: string) => {
        if (!status) { handleReset(); return; }
        setFormValues({ ...EMPTY_SEARCH, status });
        setSearchParams({ page: 0, size: 100, status: status as SearchLeadsStatus });
    };

    // Cancel confirmation + toast state.
    const user = useSelector((state: RootState) => state.auth.user);
    const [notesTarget, setNotesTarget] = useState<LoanLead | null>(null);
    const [editTarget, setEditTarget] = useState<LoanLead | null>(null);
    const [cancelTarget, setCancelTarget] = useState<LoanLead | null>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [toast, setToast] = useState<{ show: boolean; bg: 'success' | 'danger'; message: string } | null>(null);

    const handleCreateLead = () => {
        const modalElement = document.getElementById('CreateLead');
        if (!modalElement) return;
        modalElement.removeAttribute('data-from-customer');
        modalElement.removeAttribute('data-view-only');
        modalElement.removeAttribute('data-lead-data');
        modalElement.removeAttribute('data-customer-data');
        const form = modalElement.querySelector('form') as HTMLFormElement | null;
        if (form) {
            form.reset();
            form.querySelectorAll('input, select, textarea').forEach(el => { (el as HTMLInputElement).disabled = false; });
            const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            if (submitButton) submitButton.disabled = false;
            const modalTitle = modalElement.querySelector('.modal-title') as HTMLElement | null;
            if (modalTitle) modalTitle.textContent = 'Create New Lead';
        }
        new BootstrapModal(modalElement).show();
    };

    const handleViewLead = (id: string) => {
        const lead = displayedLeads.find(l => l.id === id);
        navigate(`/leads/${id}`, { state: { lead } });
    };

    const closeCancel = () => { setCancelTarget(null); setCancelReason(''); };

    const confirmCancel = async () => {
        if (!cancelTarget?.id || !cancelReason.trim()) return;
        setCancelling(true);
        try {
            await cancelLead(cancelTarget.id, {
                reason: cancelReason.trim(),
                ...(user?.name || user?.username ? { cancelledBy: user?.name || user?.username } : {}),
            });
            setToast({ show: true, bg: 'success', message: 'Lead cancelled. It is kept under Cancelled.' });
            queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads'] });
            closeCancel();
        } catch (e: any) {
            setToast({ show: true, bg: 'danger', message: e?.response?.data?.message || 'The lead could not be cancelled' });
        } finally {
            setCancelling(false);
        }
    };

    const columns: TableColumn<LoanLead>[] = [
        {
            name: 'Customer Reference',
            selector: row => row.customerReference || '',
            sortable: true,
            cell: row => (
                <div className="d-flex align-items-center">
                    <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-hashtag text-primary" style={{ display: 'block', fontSize: '0.8rem' }}></i>
                    </div>
                    {row.customerReference
                        ? <span className="fw-semibold text-primary">{row.customerReference}</span>
                        : <span className="text-muted">—</span>}
                </div>
            ),
            minWidth: '190px',
        },
        {
            name: 'Loan Type',
            selector: row => row.loanType || '',
            sortable: true,
            cell: row => row.loanType === 'PERSONAL'
                ? <Badge bg="info" className="px-2 py-2"><i className="fa fa-user me-1"></i>Personal</Badge>
                : <Badge bg="secondary" className="px-2 py-2"><i className="fa fa-building me-1"></i>Business</Badge>,
            minWidth: '130px',
        },
        {
            name: 'Company',
            selector: row => row.registeredName || '',
            sortable: true,
            cell: row => <span className="fw-semibold" style={{ wordBreak: 'break-word' }}>{row.registeredName || (row.loanType === 'PERSONAL' ? '—' : 'N/A')}</span>,
            minWidth: '180px',
            wrap: true,
        },
        {
            name: 'Contact Person',
            selector: row => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
            sortable: true,
            minWidth: '170px',
            wrap: true,
        },
        {
            name: 'Form / Product',
            selector: row => row.formName || '',
            sortable: true,
            minWidth: '170px',
            wrap: true,
        },
        {
            name: 'Channel',
            selector: row => row.channel || '',
            sortable: true,
            cell: row => {
                const meta = CHANNEL_META[row.channel || ''] ?? { label: row.channel || 'N/A', icon: 'fa-question-circle', bg: '#f1f3f5', color: '#495057' };
                return (
                    <span className="badge d-inline-flex align-items-center gap-1 px-2 py-2"
                        style={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '0.75rem', borderRadius: 8 }}>
                        <i className={`fa ${meta.icon}`}></i>{meta.label}
                    </span>
                );
            },
            minWidth: '175px',
        },
        {
            name: 'Amount',
            selector: row => row.loanAmount,
            sortable: true,
            format: row => (row.loanAmount ?? 0).toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' }),
            minWidth: '130px',
        },
        {
            name: 'Status',
            selector: row => row.status || '',
            sortable: true,
            cell: row => {
                const meta = STATUS_META[row.status || ''] ?? { label: row.status || 'N/A', variant: 'secondary', icon: 'fa-circle' };
                return (
                    <div className="py-1">
                        <Badge bg={meta.variant} className="px-3 py-2">
                            <i className={`fa ${meta.icon} me-1`}></i>{meta.label}
                        </Badge>
                        {row.status === 'CANCELLED' && row.cancellationReason && (
                            <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: 180 }} title={row.cancellationReason}>
                                {row.cancellationReason}
                            </div>
                        )}
                    </div>
                );
            },
            minWidth: '140px',
        },
        {
            name: 'Created',
            selector: row => row.createdDate || '',
            sortable: true,
            format: row => formatDateTime(row.createdDate),
            minWidth: '185px',
        },
        {
            name: 'Last Comment',
            cell: row => {
                const note = row.id ? lastNoteByLeadId.get(row.id) : undefined;
                if (!note) {
                    return <span className="text-muted fst-italic">No comments</span>;
                }
                return (
                    <div className="py-2" title={note.note}>
                        <div className="text-truncate" style={{ maxWidth: '260px' }}>{note.note}</div>
                        <small className="text-muted">
                            {note.createdBy || 'Unknown'} · {formatDateTime(note.createdDate)}
                        </small>
                    </div>
                );
            },
            minWidth: '280px',
            wrap: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <Dropdown align="end">
                    <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        id={`lead-actions-${row.id}`}
                        className="rounded-pill d-flex align-items-center no-caret"
                    >
                        <span>Actions</span>
                        <i className="fa fa-caret-down ms-2"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item className="action-view" onClick={() => handleViewLead(row.id || '')}>
                            <i className="fa fa-eye me-2"></i>
                            View Lead
                        </Dropdown.Item>
                        {/* Only a NEW lead can be edited: once converted the application holds the details. */}
                        {row.status === 'NEW' && (
                            <Dropdown.Item className="action-view" onClick={() => setEditTarget(row)}>
                                <i className="fa fa-pencil me-2"></i>
                                Edit Lead
                            </Dropdown.Item>
                        )}
                        <Dropdown.Item className="action-view" onClick={() => setNotesTarget(row)}>
                            <i className="fa fa-sticky-note me-2"></i>
                            Notes
                        </Dropdown.Item>
                        {canCancelLead(row.status) && (
                            <Dropdown.Item className="action-deactivate" onClick={() => setCancelTarget(row)}>
                                <i className="fa fa-ban me-2"></i>
                                Cancel Lead
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            ),
            minWidth: '150px',
            ignoreRowClick: true,
        },
    ];

    return (
        <>
            {/* Header */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                                </li>
                                <li className="breadcrumb-item text-white-50">Applications</li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Leads</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h1 className="fs-2 fw-bold mb-2 text-white">Leads</h1>
                            <p className="mb-0 text-white fs-6 opacity-75">
                                Manage prospective loan customers and convert them into applications
                            </p>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <div className="d-flex gap-3 justify-content-md-end flex-wrap align-items-center">
                                <Button
                                    variant="light"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{ backgroundColor: '#ffffff', color: '#495057', border: '2px solid rgba(255,255,255,0.8)', fontSize: '0.9rem', minWidth: '160px' }}
                                    onClick={() => setShowAdvancedSearch(s => !s)}
                                >
                                    <i className={`fa ${showAdvancedSearch ? 'fa-times' : 'fa-search'} me-2`} style={{ fontSize: '0.85rem' }}></i>
                                    {showAdvancedSearch ? 'Hide Search' : 'Advanced Search'}
                                </Button>
                                <Button
                                    variant="success"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{ backgroundColor: '#ffffff', color: '#28a745', border: '2px solid #28a745', fontSize: '0.9rem', minWidth: '160px' }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#28a745'; e.currentTarget.style.color = '#fff'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#28a745'; }}
                                    onClick={handleCreateLead}
                                >
                                    <i className="fa fa-plus-circle me-2" style={{ fontSize: '0.85rem' }}></i>
                                    Create New Lead
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">

                    {/* Summary Cards */}
                    <div className="row g-4 mb-4 row-cols-2 row-cols-md-3 row-cols-xl-5">
                        {[
                            { label: 'Total Leads', value: totalLeads,       icon: 'fa-users',        color: 'primary',   border: 'rgba(0,123,255,0.2)', status: undefined },
                            { label: 'New',         value: counts.NEW,       icon: 'fa-star',         color: 'info',      border: 'rgba(23,162,184,0.2)', status: 'NEW' },
                            { label: 'Completed',   value: counts.COMPLETED, icon: 'fa-check-circle', color: 'success',   border: 'rgba(40,167,69,0.2)', status: 'COMPLETED' },
                            { label: 'Rejected',    value: counts.REJECTED,  icon: 'fa-times-circle', color: 'danger',    border: 'rgba(220,53,69,0.2)', status: 'REJECTED' },
                            { label: 'Cancelled',   value: counts.CANCELLED, icon: 'fa-ban',          color: 'secondary', border: 'rgba(108,117,125,0.25)', status: 'CANCELLED' },
                        ].map(card => (
                            <div key={card.label} className="col">
                                <Card className="border-0 shadow-sm h-100" role="button"
                                    title={card.status ? `Show ${card.label.toLowerCase()} leads` : 'Show the default list'}
                                    onClick={() => showStatus(card.status)}
                                    style={searchParams?.status && searchParams.status === card.status ? { outline: '2px solid #198754' } : undefined}>
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted small mb-1">{card.label}</p>
                                                <h3 className={`mb-0 fw-bold text-${card.color}`}>{card.value}</h3>
                                            </div>
                                            <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: `1px solid ${card.border}` }}>
                                                <i className={`fa ${card.icon} fa-2x text-${card.color}`} style={{ display: 'block' }}></i>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>

                    {/* Advanced Search — fields map to the leads table columns */}
                    {showAdvancedSearch && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-white border-0 py-3">
                                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                                            <i className="fa fa-search text-primary me-2"></i>Advanced Search
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form>
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Customer Reference</Form.Label>
                                                        <Form.Control type="text" placeholder="e.g. LEAD-20260706-K7Q2"
                                                            value={formValues.customerReference}
                                                            onChange={e => setFormValues(p => ({ ...p, customerReference: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Company</Form.Label>
                                                        <Form.Control type="text" placeholder="Enter company name"
                                                            value={formValues.company}
                                                            onChange={e => setFormValues(p => ({ ...p, company: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Registration Number</Form.Label>
                                                        <Form.Control type="text" placeholder="e.g. 2025/123456/07"
                                                            value={formValues.registrationNumber}
                                                            onChange={e => setFormValues(p => ({ ...p, registrationNumber: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Contact Person</Form.Label>
                                                        <Form.Control type="text" placeholder="Enter contact name"
                                                            value={formValues.contactPerson}
                                                            onChange={e => setFormValues(p => ({ ...p, contactPerson: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Loan Type</Form.Label>
                                                        <Form.Select value={formValues.loanType}
                                                            onChange={e => setFormValues(p => ({ ...p, loanType: e.target.value }))}>
                                                            <option value="">All</option>
                                                            <option value="BUSINESS">Business</option>
                                                            <option value="PERSONAL">Personal</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Status</Form.Label>
                                                        <Form.Select value={formValues.status}
                                                            onChange={e => setFormValues(p => ({ ...p, status: e.target.value }))}>
                                                            <option value="">New &amp; Completed (default)</option>
                                                            <option value="NEW">New</option>
                                                            <option value="COMPLETED">Completed</option>
                                                            <option value="REJECTED">Rejected</option>
                                                            <option value="EXPIRED">Expired</option>
                                                            <option value="CANCELLED">Cancelled</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-2">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Amount From</Form.Label>
                                                        <Form.Control type="number" min="0" placeholder="0"
                                                            value={formValues.amountFrom}
                                                            onChange={e => setFormValues(p => ({ ...p, amountFrom: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-2">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Amount To</Form.Label>
                                                        <Form.Control type="number" min="0" placeholder="Any"
                                                            value={formValues.amountTo}
                                                            onChange={e => setFormValues(p => ({ ...p, amountTo: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Created From</Form.Label>
                                                        <Form.Control type="date"
                                                            value={formValues.createdFrom}
                                                            onChange={e => setFormValues(p => ({ ...p, createdFrom: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Created To</Form.Label>
                                                        <Form.Control type="date"
                                                            value={formValues.createdTo}
                                                            onChange={e => setFormValues(p => ({ ...p, createdTo: e.target.value }))} />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 d-flex justify-content-end gap-2">
                                                    <Button variant="outline-secondary" onClick={handleReset}>
                                                        <i className="fa fa-undo me-2"></i>Reset
                                                    </Button>
                                                    <Button variant="primary" onClick={handleSearch}>
                                                        <i className="fa fa-search me-2"></i>Search
                                                    </Button>
                                                </div>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* Leads Table */}
                    <div className="row">
                        <div className="col-12">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-0 py-3 px-4">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                                            <i className="fa fa-users text-primary me-2"></i>
                                            Leads
                                            <Badge bg="primary" className="ms-2">{displayedLeads.length}</Badge>
                                        </h5>
                                        <span className="text-muted small">
                                            {isSearching
                                                ? (searchParams?.status ? `Showing ${(STATUS_META[searchParams.status]?.label || searchParams.status).toLowerCase()} leads` : 'Search results')
                                                : 'Showing New and Completed leads'}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {error && (
                                        <Alert variant="danger" className="m-3">
                                            <i className="fa fa-exclamation-circle me-2"></i>
                                            {error instanceof Error ? error.message : 'Failed to load leads. Please try again.'}
                                        </Alert>
                                    )}
                                    <div className="table-responsive">
                                        <DataTable
                                            columns={columns}
                                            data={displayedLeads}
                                            pagination
                                            highlightOnHover
                                            striped
                                            responsive
                                            progressPending={isLoading}
                                            progressComponent={
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-3 text-muted">Loading leads...</p>
                                                </div>
                                            }
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                                            paginationComponentOptions={{ rowsPerPageText: 'Rows per page:', rangeSeparatorText: 'of' }}
                                            noDataComponent={
                                                <div className="text-center py-5">
                                                    <i className="fa fa-users fa-3x text-muted mb-3"></i>
                                                    <h5 className="text-muted">{isSearching ? 'No Matching Leads' : 'No Leads'}</h5>
                                                    <p className="text-muted mb-0">{isSearching ? 'No leads match your search criteria.' : 'There are no new or completed leads.'}</p>
                                                </div>
                                            }
                                            customStyles={{
                                                headRow:   { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' } },
                                                headCells: { style: { paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem', paddingBottom: '1rem' } },
                                                cells:     { style: { paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', fontSize: '0.85rem' } },
                                                rows:      { style: { minHeight: '55px', borderBottom: '1px solid #f8f9fa' } },
                                            }}
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            <EditLeadModal
                lead={editTarget}
                onHide={() => setEditTarget(null)}
                onSaved={() => setToast({ show: true, bg: 'success', message: 'Lead updated' })}
            />

            {/* Notes */}
            <LeadNotesModal
                leadId={notesTarget?.id ?? null}
                leadName={notesTarget?.registeredName
                    || `${notesTarget?.firstName || ''} ${notesTarget?.lastName || ''}`.trim()
                    || notesTarget?.customerReference}
                onHide={() => setNotesTarget(null)}
            />

            <Modal show={!!cancelTarget} onHide={() => !cancelling && closeCancel()} centered backdrop={cancelling ? 'static' : true}>
                <Modal.Header closeButton={!cancelling} className="border-0 bg-warning">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        <i className="fa fa-ban"></i> Cancel Lead
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-3">
                        The lead is kept, marked Cancelled with your reason, and its work item is closed.
                        You can still find it under <strong>Cancelled</strong>.
                    </p>
                    {cancelTarget && (
                        <div className="bg-light rounded p-3 mb-3">
                            <div><span className="text-muted small">Reference:</span> <strong>{cancelTarget.customerReference || '—'}</strong></div>
                            <div><span className="text-muted small">Company:</span> <strong>{cancelTarget.registeredName || 'N/A'}</strong></div>
                            <div><span className="text-muted small">Contact:</span> <strong>{`${cancelTarget.firstName || ''} ${cancelTarget.lastName || ''}`.trim() || 'N/A'}</strong></div>
                        </div>
                    )}
                    <Form.Group controlId="cancel-lead-reason">
                        <Form.Label className="fw-semibold">Reason for cancelling <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="textarea" rows={3} maxLength={1000} value={cancelReason}
                            placeholder="e.g. Client found funding elsewhere"
                            onChange={e => setCancelReason(e.target.value)} disabled={cancelling} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="warning" onClick={confirmCancel} disabled={cancelling || !cancelReason.trim()}>
                        {cancelling
                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Cancelling…</>
                            : <><i className="fa fa-ban me-2"></i>Cancel Lead</>}
                    </Button>
                    <Button variant="outline-secondary" onClick={closeCancel} disabled={cancelling}>
                        Keep Lead
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast */}
            <ToastContainer position="top-end" className="p-3">
                <Toast bg={toast?.bg} show={!!toast?.show} autohide delay={4000} onClose={() => setToast(null)}>
                    <Toast.Body className="text-white d-flex align-items-center">
                        <i className={`fa ${toast?.bg === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                        <span className="fw-semibold">{toast?.message}</span>
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default Leads;
